import { useState, useRef, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import HTMLReactParser from 'html-react-parser';
import { capitalize } from '../utils';
import { useTranslation } from 'react-i18next';

// ─── Language → default country code ─────────────────────────────────────────
const LANG_TO_COUNTRY_CODE = {
  fr: 'FR', en: 'GB', es: 'ES', de: 'DE', it: 'IT',
  pt: 'PT', ar: 'MA', zh: 'CN', ja: 'JP', ko: 'KR',
  ru: 'RU', nl: 'NL', tr: 'TR', pl: 'PL', sv: 'SE',
}

// ─── Country list ─────────────────────────────────────────────────────────────
const COUNTRIES = [
  { "code": "AF", "dial": "+93", "flag": "🇦🇫", "name": "Afghanistan" },
  { "code": "ZA", "dial": "+27", "flag": "🇿🇦", "name": "Afrique du Sud" },
  { "code": "AX", "dial": "+358", "flag": "🇦🇽", "name": "Îles Åland" },
  { "code": "AL", "dial": "+355", "flag": "🇦🇱", "name": "Albanie" },
  { "code": "DZ", "dial": "+213", "flag": "🇩🇿", "name": "Algérie" },
  { "code": "DE", "dial": "+49", "flag": "🇩🇪", "name": "Allemagne" },
  { "code": "AD", "dial": "+376", "flag": "🇦🇩", "name": "Andorre" },
  { "code": "AO", "dial": "+244", "flag": "🇦🇴", "name": "Angola" },
  { "code": "AI", "dial": "+1264", "flag": "🇦🇮", "name": "Anguilla" },
  { "code": "AQ", "dial": "+672", "flag": "🇦🇶", "name": "Antarctique" },
  { "code": "AG", "dial": "+1268", "flag": "🇦🇬", "name": "Antigua-et-Barbuda" },
  { "code": "SA", "dial": "+966", "flag": "🇸🇦", "name": "Arabie saoudite" },
  { "code": "AR", "dial": "+54", "flag": "🇦🇷", "name": "Argentine" },
  { "code": "AM", "dial": "+374", "flag": "🇦🇲", "name": "Arménie" },
  { "code": "AW", "dial": "+297", "flag": "🇦🇼", "name": "Aruba" },
  { "code": "AU", "dial": "+61", "flag": "🇦🇺", "name": "Australie" },
  { "code": "AT", "dial": "+43", "flag": "🇦🇹", "name": "Autriche" },
  { "code": "AZ", "dial": "+994", "flag": "🇦🇿", "name": "Azerbaïdjan" },
  { "code": "BS", "dial": "+1242", "flag": "🇧🇸", "name": "Bahamas" },
  { "code": "BH", "dial": "+973", "flag": "🇧🇭", "name": "Bahreïn" },
  { "code": "BD", "dial": "+880", "flag": "🇧🇩", "name": "Bangladesh" },
  { "code": "BB", "dial": "+1246", "flag": "🇧🇧", "name": "Barbade" },
  { "code": "BE", "dial": "+32", "flag": "🇧🇪", "name": "Belgique" },
  { "code": "BZ", "dial": "+501", "flag": "🇧🇿", "name": "Belize" },
  { "code": "BJ", "dial": "+229", "flag": "🇧🇯", "name": "Bénin" },
  { "code": "BM", "dial": "+1441", "flag": "🇧🇲", "name": "Bermudes" },
  { "code": "BY", "dial": "+375", "flag": "🇧🇾", "name": "Biélorussie" },
  { "code": "MM", "dial": "+95", "flag": "🇲🇲", "name": "Myanmar (Birmanie)" },
  { "code": "BO", "dial": "+591", "flag": "🇧🇴", "name": "Bolivie" },
  { "code": "BQ", "dial": "+599", "flag": "🇧🇶", "name": "Bonaire, Saint-Eustache et Saba" },
  { "code": "BA", "dial": "+387", "flag": "🇧🇦", "name": "Bosnie-Herzégovine" },
  { "code": "BW", "dial": "+267", "flag": "🇧🇼", "name": "Botswana" },
  { "code": "BR", "dial": "+55", "flag": "🇧🇷", "name": "Brésil" },
  { "code": "BN", "dial": "+673", "flag": "🇧🇳", "name": "Brunéi" },
  { "code": "BG", "dial": "+359", "flag": "🇧🇬", "name": "Bulgarie" },
  { "code": "BF", "dial": "+226", "flag": "🇧🇫", "name": "Burkina Faso" },
  { "code": "BI", "dial": "+257", "flag": "🇧🇮", "name": "Burundi" },
  { "code": "KH", "dial": "+855", "flag": "🇰🇭", "name": "Cambodge" },
  { "code": "CM", "dial": "+237", "flag": "🇨🇲", "name": "Cameroun" },
  { "code": "CA", "dial": "+1", "flag": "🇨🇦", "name": "Canada" },
  { "code": "CV", "dial": "+238", "flag": "🇨🇻", "name": "Cap-Vert" },
  { "code": "CF", "dial": "+236", "flag": "🇨🇫", "name": "République centrafricaine" },
  { "code": "CL", "dial": "+56", "flag": "🇨🇱", "name": "Chili" },
  { "code": "CN", "dial": "+86", "flag": "🇨🇳", "name": "Chine" },
  { "code": "CY", "dial": "+357", "flag": "🇨🇾", "name": "Chypre" },
  { "code": "CO", "dial": "+57", "flag": "🇨🇴", "name": "Colombie" },
  { "code": "KM", "dial": "+269", "flag": "🇰🇲", "name": "Comores" },
  { "code": "CG", "dial": "+242", "flag": "🇨🇬", "name": "République du Congo" },
  { "code": "CD", "dial": "+243", "flag": "🇨🇩", "name": "République démocratique du Congo" },
  { "code": "CK", "dial": "+682", "flag": "🇨🇰", "name": "Îles Cook" },
  { "code": "KP", "dial": "+850", "flag": "🇰🇵", "name": "Corée du Nord" },
  { "code": "KR", "dial": "+82", "flag": "🇰🇷", "name": "Corée du Sud" },
  { "code": "CR", "dial": "+506", "flag": "🇨🇷", "name": "Costa Rica" },
  { "code": "CI", "dial": "+225", "flag": "🇨🇮", "name": "Côte d'Ivoire" },
  { "code": "HR", "dial": "+385", "flag": "🇭🇷", "name": "Croatie" },
  { "code": "CU", "dial": "+53", "flag": "🇨🇺", "name": "Cuba" },
  { "code": "CW", "dial": "+599", "flag": "🇨🇼", "name": "Curaçao" },
  { "code": "DK", "dial": "+45", "flag": "🇩🇰", "name": "Danemark" },
  { "code": "DJ", "dial": "+253", "flag": "🇩🇯", "name": "Djibouti" },
  { "code": "DM", "dial": "+1767", "flag": "🇩🇲", "name": "Dominique" },
  { "code": "EG", "dial": "+20", "flag": "🇪🇬", "name": "Égypte" },
  { "code": "AE", "dial": "+971", "flag": "🇦🇪", "name": "Émirats arabes unis" },
  { "code": "EC", "dial": "+593", "flag": "🇪🇨", "name": "Équateur" },
  { "code": "ER", "dial": "+291", "flag": "🇪🇷", "name": "Érythrée" },
  { "code": "ES", "dial": "+34", "flag": "🇪🇸", "name": "Espagne" },
  { "code": "EE", "dial": "+372", "flag": "🇪🇪", "name": "Estonie" },
  { "code": "US", "dial": "+1", "flag": "🇺🇸", "name": "États-Unis" },
  { "code": "ET", "dial": "+251", "flag": "🇪🇹", "name": "Éthiopie" },
  { "code": "FK", "dial": "+500", "flag": "🇫🇰", "name": "Îles Malouines" },
  { "code": "FO", "dial": "+298", "flag": "🇫🇴", "name": "Îles Féroé" },
  { "code": "FJ", "dial": "+679", "flag": "🇫🇯", "name": "Fidji" },
  { "code": "FI", "dial": "+358", "flag": "🇫🇮", "name": "Finlande" },
  { "code": "FR", "dial": "+33", "flag": "🇫🇷", "name": "France" },
  { "code": "GA", "dial": "+241", "flag": "🇬🇦", "name": "Gabon" },
  { "code": "GM", "dial": "+220", "flag": "🇬🇲", "name": "Gambie" },
  { "code": "GE", "dial": "+995", "flag": "🇬🇪", "name": "Géorgie" },
  { "code": "GH", "dial": "+233", "flag": "🇬🇭", "name": "Ghana" },
  { "code": "GI", "dial": "+350", "flag": "🇬🇮", "name": "Gibraltar" },
  { "code": "GR", "dial": "+30", "flag": "🇬🇷", "name": "Grèce" },
  { "code": "GD", "dial": "+1473", "flag": "🇬🇩", "name": "Grenade" },
  { "code": "GL", "dial": "+299", "flag": "🇬🇱", "name": "Groenland" },
  { "code": "GP", "dial": "+590", "flag": "🇬🇵", "name": "Guadeloupe" },
  { "code": "GU", "dial": "+1671", "flag": "🇬🇺", "name": "Guam" },
  { "code": "GT", "dial": "+502", "flag": "🇬🇹", "name": "Guatemala" },
  { "code": "GG", "dial": "+44", "flag": "🇬🇬", "name": "Guernesey" },
  { "code": "GN", "dial": "+224", "flag": "🇬🇳", "name": "Guinée" },
  { "code": "GW", "dial": "+245", "flag": "🇬🇼", "name": "Guinée-Bissau" },
  { "code": "GQ", "dial": "+240", "flag": "🇬🇶", "name": "Guinée équatoriale" },
  { "code": "GY", "dial": "+592", "flag": "🇬🇾", "name": "Guyana" },
  { "code": "GF", "dial": "+594", "flag": "🇬🇫", "name": "Guyane" },
  { "code": "HT", "dial": "+509", "flag": "🇭🇹", "name": "Haïti" },
  { "code": "HN", "dial": "+504", "flag": "🇭🇳", "name": "Honduras" },
  { "code": "HK", "dial": "+852", "flag": "🇭🇰", "name": "Hong Kong" },
  { "code": "HU", "dial": "+36", "flag": "🇭🇺", "name": "Hongrie" },
  { "code": "IM", "dial": "+44", "flag": "🇮🇲", "name": "Île de Man" },
  { "code": "IN", "dial": "+91", "flag": "🇮🇳", "name": "Inde" },
  { "code": "ID", "dial": "+62", "flag": "🇮🇩", "name": "Indonésie" },
  { "code": "IR", "dial": "+98", "flag": "🇮🇷", "name": "Iran" },
  { "code": "IQ", "dial": "+964", "flag": "🇮🇶", "name": "Irak" },
  { "code": "IE", "dial": "+353", "flag": "🇮🇪", "name": "Irlande" },
  { "code": "IS", "dial": "+354", "flag": "🇮🇸", "name": "Islande" },
  { "code": "IL", "dial": "+972", "flag": "🇮🇱", "name": "Israël" },
  { "code": "IT", "dial": "+39", "flag": "🇮🇹", "name": "Italie" },
  { "code": "JM", "dial": "+1876", "flag": "🇯🇲", "name": "Jamaïque" },
  { "code": "JP", "dial": "+81", "flag": "🇯🇵", "name": "Japon" },
  { "code": "JE", "dial": "+44", "flag": "🇯🇪", "name": "Jersey" },
  { "code": "JO", "dial": "+962", "flag": "🇯🇴", "name": "Jordanie" },
  { "code": "KZ", "dial": "+7", "flag": "🇰🇿", "name": "Kazakhstan" },
  { "code": "KE", "dial": "+254", "flag": "🇰🇪", "name": "Kenya" },
  { "code": "KG", "dial": "+996", "flag": "🇰🇬", "name": "Kirghizistan" },
  { "code": "KI", "dial": "+686", "flag": "🇰🇮", "name": "Kiribati" },
  { "code": "XK", "dial": "+383", "flag": "🇽🇰", "name": "Kosovo" },
  { "code": "KW", "dial": "+965", "flag": "🇰🇼", "name": "Koweït" },
  { "code": "LA", "dial": "+856", "flag": "🇱🇦", "name": "Laos" },
  { "code": "LS", "dial": "+266", "flag": "🇱🇸", "name": "Lesotho" },
  { "code": "LV", "dial": "+371", "flag": "🇱🇻", "name": "Lettonie" },
  { "code": "LB", "dial": "+961", "flag": "🇱🇧", "name": "Liban" },
  { "code": "LR", "dial": "+231", "flag": "🇱🇷", "name": "Liberia" },
  { "code": "LY", "dial": "+218", "flag": "🇱🇾", "name": "Libye" },
  { "code": "LI", "dial": "+423", "flag": "🇱🇮", "name": "Liechtenstein" },
  { "code": "LT", "dial": "+370", "flag": "🇱🇹", "name": "Lituanie" },
  { "code": "LU", "dial": "+352", "flag": "🇱🇺", "name": "Luxembourg" },
  { "code": "MO", "dial": "+853", "flag": "🇲🇴", "name": "Macao" },
  { "code": "MK", "dial": "+389", "flag": "🇲🇰", "name": "Macédoine du Nord" },
  { "code": "MG", "dial": "+261", "flag": "🇲🇬", "name": "Madagascar" },
  { "code": "MY", "dial": "+60", "flag": "🇲🇾", "name": "Malaisie" },
  { "code": "MW", "dial": "+265", "flag": "🇲🇼", "name": "Malawi" },
  { "code": "MV", "dial": "+960", "flag": "🇲🇻", "name": "Maldives" },
  { "code": "ML", "dial": "+223", "flag": "🇲🇱", "name": "Mali" },
  { "code": "MT", "dial": "+356", "flag": "🇲🇹", "name": "Malte" },
  { "code": "MA", "dial": "+212", "flag": "🇲🇦", "name": "Maroc" },
  { "code": "MQ", "dial": "+596", "flag": "🇲🇶", "name": "Martinique" },
  { "code": "MU", "dial": "+230", "flag": "🇲🇺", "name": "Maurice" },
  { "code": "MR", "dial": "+222", "flag": "🇲🇷", "name": "Mauritanie" },
  { "code": "YT", "dial": "+262", "flag": "🇾🇹", "name": "Mayotte" },
  { "code": "MX", "dial": "+52", "flag": "🇲🇽", "name": "Mexique" },
  { "code": "FM", "dial": "+691", "flag": "🇫🇲", "name": "Micronésie" },
  { "code": "MD", "dial": "+373", "flag": "🇲🇩", "name": "Moldavie" },
  { "code": "MC", "dial": "+377", "flag": "🇲🇨", "name": "Monaco" },
  { "code": "MN", "dial": "+976", "flag": "🇲🇳", "name": "Mongolie" },
  { "code": "ME", "dial": "+382", "flag": "🇲🇪", "name": "Monténégro" },
  { "code": "MS", "dial": "+1664", "flag": "🇲🇸", "name": "Montserrat" },
  { "code": "MZ", "dial": "+258", "flag": "🇲🇿", "name": "Mozambique" },
  { "code": "NA", "dial": "+264", "flag": "🇳🇦", "name": "Namibie" },
  { "code": "NR", "dial": "+674", "flag": "🇳🇷", "name": "Nauru" },
  { "code": "NP", "dial": "+977", "flag": "🇳🇵", "name": "Népal" },
  { "code": "NI", "dial": "+505", "flag": "🇳🇮", "name": "Nicaragua" },
  { "code": "NE", "dial": "+227", "flag": "🇳🇪", "name": "Niger" },
  { "code": "NG", "dial": "+234", "flag": "🇳🇬", "name": "Nigeria" },
  { "code": "NU", "dial": "+683", "flag": "🇳🇺", "name": "Niue" },
  { "code": "NF", "dial": "+672", "flag": "🇳🇫", "name": "Île Norfolk" },
  { "code": "NO", "dial": "+47", "flag": "🇳🇴", "name": "Norvège" },
  { "code": "NC", "dial": "+687", "flag": "🇳🇨", "name": "Nouvelle-Calédonie" },
  { "code": "NZ", "dial": "+64", "flag": "🇳🇿", "name": "Nouvelle-Zélande" },
  { "code": "OM", "dial": "+968", "flag": "🇴🇲", "name": "Oman" },
  { "code": "UG", "dial": "+256", "flag": "🇺🇬", "name": "Ouganda" },
  { "code": "UZ", "dial": "+998", "flag": "🇺🇿", "name": "Ouzbékistan" },
  { "code": "PK", "dial": "+92", "flag": "🇵🇰", "name": "Pakistan" },
  { "code": "PW", "dial": "+680", "flag": "🇵🇼", "name": "Palaos" },
  { "code": "PS", "dial": "+970", "flag": "🇵🇸", "name": "Palestine" },
  { "code": "PA", "dial": "+507", "flag": "🇵🇦", "name": "Panama" },
  { "code": "PG", "dial": "+675", "flag": "🇵🇬", "name": "Papouasie-Nouvelle-Guinée" },
  { "code": "PY", "dial": "+595", "flag": "🇵🇾", "name": "Paraguay" },
  { "code": "NL", "dial": "+31", "flag": "🇳🇱", "name": "Pays-Bas" },
  { "code": "PE", "dial": "+51", "flag": "🇵🇪", "name": "Pérou" },
  { "code": "PH", "dial": "+63", "flag": "🇵🇭", "name": "Philippines" },
  { "code": "PN", "dial": "+64", "flag": "🇵🇳", "name": "Îles Pitcairn" },
  { "code": "PL", "dial": "+48", "flag": "🇵🇱", "name": "Pologne" },
  { "code": "PF", "dial": "+689", "flag": "🇵🇫", "name": "Polynésie française" },
  { "code": "PR", "dial": "+1787", "flag": "🇵🇷", "name": "Porto Rico" },
  { "code": "PT", "dial": "+351", "flag": "🇵🇹", "name": "Portugal" },
  { "code": "QA", "dial": "+974", "flag": "🇶🇦", "name": "Qatar" },
  { "code": "RE", "dial": "+262", "flag": "🇷🇪", "name": "La Réunion" },
  { "code": "RO", "dial": "+40", "flag": "🇷🇴", "name": "Roumanie" },
  { "code": "GB", "dial": "+44", "flag": "🇬🇧", "name": "Royaume-Uni" },
  { "code": "RU", "dial": "+7", "flag": "🇷🇺", "name": "Russie" },
  { "code": "RW", "dial": "+250", "flag": "🇷🇼", "name": "Rwanda" },
  { "code": "EH", "dial": "+212", "flag": "🇪🇭", "name": "Sahara occidental" },
  { "code": "BL", "dial": "+590", "flag": "🇧🇱", "name": "Saint-Barthélemy" },
  { "code": "KN", "dial": "+1869", "flag": "🇰🇳", "name": "Saint-Christophe-et-Niévès" },
  { "code": "SM", "dial": "+378", "flag": "🇸🇲", "name": "Saint-Marin" },
  { "code": "MF", "dial": "+590", "flag": "🇲🇫", "name": "Saint-Martin (Antilles françaises)" },
  { "code": "SX", "dial": "+1721", "flag": "🇸🇽", "name": "Saint-Martin (Royaume des Pays-Bas)" },
  { "code": "PM", "dial": "+508", "flag": "🇵🇲", "name": "Saint-Pierre-et-Miquelon" },
  { "code": "VC", "dial": "+1784", "flag": "🇻🇨", "name": "Saint-Vincent-et-les-Grenadines" },
  { "code": "SH", "dial": "+290", "flag": "🇸🇭", "name": "Sainte-Hélène, Ascension et Tristan da Cunha" },
  { "code": "LC", "dial": "+1758", "flag": "🇱🇨", "name": "Sainte-Lucie" },
  { "code": "SB", "dial": "+677", "flag": "🇸🇧", "name": "Îles Salomon" },
  { "code": "WS", "dial": "+685", "flag": "🇼🇸", "name": "Samoa" },
  { "code": "AS", "dial": "+1684", "flag": "🇦🇸", "name": "Samoa américaines" },
  { "code": "ST", "dial": "+239", "flag": "🇸🇹", "name": "Sao Tomé-et-Principe" },
  { "code": "SN", "dial": "+221", "flag": "🇸🇳", "name": "Sénégal" },
  { "code": "RS", "dial": "+381", "flag": "🇷🇸", "name": "Serbie" },
  { "code": "SC", "dial": "+248", "flag": "🇸🇨", "name": "Seychelles" },
  { "code": "SL", "dial": "+232", "flag": "🇸🇱", "name": "Sierra Leone" },
  { "code": "SG", "dial": "+65", "flag": "🇸🇬", "name": "Singapour" },
  { "code": "SK", "dial": "+421", "flag": "🇸🇰", "name": "Slovaquie" },
  { "code": "SI", "dial": "+386", "flag": "🇸🇮", "name": "Slovénie" },
  { "code": "SO", "dial": "+252", "flag": "🇸🇴", "name": "Somalie" },
  { "code": "SD", "dial": "+249", "flag": "🇸🇩", "name": "Soudan" },
  { "code": "SS", "dial": "+211", "flag": "🇸🇸", "name": "Soudan du Sud" },
  { "code": "LK", "dial": "+94", "flag": "🇱🇰", "name": "Sri Lanka" },
  { "code": "SE", "dial": "+46", "flag": "🇸🇪", "name": "Suède" },
  { "code": "CH", "dial": "+41", "flag": "🇨🇭", "name": "Suisse" },
  { "code": "SR", "dial": "+597", "flag": "🇸🇷", "name": "Suriname" },
  { "code": "SJ", "dial": "+47", "flag": "🇸🇯", "name": "Svalbard et Jan Mayen" },
  { "code": "SZ", "dial": "+268", "flag": "🇸🇿", "name": "Eswatini (Swaziland)" },
  { "code": "SY", "dial": "+963", "flag": "🇸🇾", "name": "Syrie" },
  { "code": "TJ", "dial": "+992", "flag": "🇹🇯", "name": "Tadjikistan" },
  { "code": "TW", "dial": "+886", "flag": "🇹🇼", "name": "Taïwan" },
  { "code": "TZ", "dial": "+255", "flag": "🇹🇿", "name": "Tanzanie" },
  { "code": "TD", "dial": "+235", "flag": "🇹🇩", "name": "Tchad" },
  { "code": "CZ", "dial": "+420", "flag": "🇨🇿", "name": "Tchéquie" },
  { "code": "TF", "dial": "+262", "flag": "🇹🇫", "name": "Terres australes et antarctiques françaises" },
  { "code": "IO", "dial": "+246", "flag": "🇮🇴", "name": "Territoire britannique de l'océan Indien" },
  { "code": "TH", "dial": "+66", "flag": "🇹🇭", "name": "Thaïlande" },
  { "code": "TL", "dial": "+670", "flag": "🇹🇱", "name": "Timor oriental" },
  { "code": "TG", "dial": "+228", "flag": "🇹🇬", "name": "Togo" },
  { "code": "TK", "dial": "+690", "flag": "🇹🇰", "name": "Tokelau" },
  { "code": "TO", "dial": "+676", "flag": "🇹🇴", "name": "Tonga" },
  { "code": "TT", "dial": "+1868", "flag": "🇹🇹", "name": "Trinité-et-Tobago" },
  { "code": "TN", "dial": "+216", "flag": "🇹🇳", "name": "Tunisie" },
  { "code": "TM", "dial": "+993", "flag": "🇹🇲", "name": "Turkménistan" },
  { "code": "TC", "dial": "+1649", "flag": "🇹🇨", "name": "Îles Turques-et-Caïques" },
  { "code": "TR", "dial": "+90", "flag": "🇹🇷", "name": "Turquie" },
  { "code": "TV", "dial": "+688", "flag": "🇹🇻", "name": "Tuvalu" },
  { "code": "UA", "dial": "+380", "flag": "🇺🇦", "name": "Ukraine" },
  { "code": "UY", "dial": "+598", "flag": "🇺🇾", "name": "Uruguay" },
  { "code": "VU", "dial": "+678", "flag": "🇻🇺", "name": "Vanuatu" },
  { "code": "VA", "dial": "+39", "flag": "🇻🇦", "name": "Vatican" },
  { "code": "VE", "dial": "+58", "flag": "🇻🇪", "name": "Venezuela" },
  { "code": "VG", "dial": "+1284", "flag": "🇻🇬", "name": "Îles Vierges britanniques" },
  { "code": "VI", "dial": "+1340", "flag": "🇻🇮", "name": "Îles Vierges des États-Unis" },
  { "code": "VN", "dial": "+84", "flag": "🇻🇳", "name": "Viêt Nam" },
  { "code": "WF", "dial": "+681", "flag": "🇼🇫", "name": "Wallis-et-Futuna" },
  { "code": "YE", "dial": "+967", "flag": "🇾🇪", "name": "Yémen" },
  { "code": "ZM", "dial": "+260", "flag": "🇿🇲", "name": "Zambie" },
  { "code": "ZW", "dial": "+263", "flag": "🇿🇼", "name": "Zimbabwe" }
];
// Parse a stored value like "+33612345678" → { country, number }
const parseValue = (val, fallback) => {
  const def = fallback ?? COUNTRIES[0];
  if (!val) return { country: def, number: '' };
  const v = val.startsWith('+') ? val : '+' + val;
  const sorted = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (v.startsWith(c.dial)) return { country: c, number: v.slice(c.dial.length) };
  }
  return { country: def, number: v.replace(/^\+/, '') };
};

// ─── Core UI component (no form context dependency) ──────────────────────────
export function PhoneInputField({ value, onChange, required, name, id, placeholder }) {
  const { i18n } = useTranslation();
  const langCountry = COUNTRIES.find(c => c.code === (LANG_TO_COUNTRY_CODE[i18n.resolvedLanguage] ?? 'FR'))
    ?? COUNTRIES.find(c => c.code === 'FR');
  const parsed = parseValue(value, langCountry);
  const [country, setCountry] = useState(parsed.country);
  const [number, setNumber] = useState(parsed.number);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Sync external value changes (e.g. defaultValue arriving async)
  useEffect(() => {
    const p = parseValue(value, langCountry);
    setCountry(p.country);
    setNumber(p.number);
  }, [value, langCountry]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const emit = (c, n) => {
    const full = n.trim() ? c.dial + n.replace(/\s/g, '') : '';
    onChange?.(full);
  };

  const handleCountrySelect = (c) => {
    setCountry(c);
    setOpen(false);
    setSearch('');
    emit(c, number);
  };

  const handleNumberChange = (e) => {
    const n = e.target.value.replace(/[^\d\s\-().]/g, '');
    setNumber(n);
    emit(country, n);
  };

  const filtered = search
    ? COUNTRIES.filter(c =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.dial.includes(search) ||
      c.code.toLowerCase().includes(search.toLowerCase())
    )
    : COUNTRIES;

  return (
    <div className="flex w-full rounded-lg border border-gray-200 dark:border-neutral-700 overflow-visible bg-white dark:bg-neutral-800 focus-within:ring-2 focus-within:ring-e2o-400 focus-within:border-transparent transition-shadow relative">
      {/* Country selector */}
      <div ref={dropdownRef} className="relative shrink-0">
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="h-full flex items-center gap-1.5 px-2.5 text-sm border-r border-gray-200 dark:border-neutral-700 hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors rounded-l-lg whitespace-nowrap"
        >
          <span className="text-base leading-none">{country.flag}</span>
          <span className="text-gray-600 dark:text-neutral-300 text-xs font-medium">{country.dial}</span>
          <svg className="w-3 h-3 text-gray-400 dark:text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl shadow-lg z-50 overflow-hidden">
            <div className="p-2 border-b border-gray-100 dark:border-neutral-700">
              <input
                type="text"
                autoFocus
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher un pays…"
                className="w-full text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-neutral-600 bg-gray-50 dark:bg-neutral-700 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none"
              />
            </div>
            <ul className="max-h-52 overflow-y-auto">
              {filtered.map(c => (
                <li key={c.code}>
                  <button
                    type="button"
                    onClick={() => handleCountrySelect(c)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors text-left ${c.code === country.code ? 'bg-e2o-50 dark:bg-e2o-900/20 font-medium' : 'text-gray-700 dark:text-neutral-200'}`}
                  >
                    <span className="text-base">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-gray-400 dark:text-neutral-500 text-xs shrink-0">{c.dial}</span>
                  </button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-3 py-4 text-sm text-gray-400 dark:text-neutral-500 text-center">Pays introuvable</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* Number input */}
      <input
        type="tel"
        name={name}
        id={id}
        required={required}
        placeholder={placeholder || '6 12 34 56 78'}
        value={number}
        onChange={handleNumberChange}
        className="flex-1 min-w-0 px-3 py-2 text-sm bg-transparent text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none"
      />
    </div>
  );
}

// ─── react-hook-form wrapper (for RegistrationForm* components) ───────────────
export function PhoneFormRow({
  name = 'port',
  placeholder = 'Mobile',
  defaultValue = '',
  required = true,
  labelClassName = '',
}) {
  const { control, formState } = useFormContext();

  return (
    <div className={`flex flex-col sm:flex-row mb-1 py-2 w-full ${formState.errors[name] ? 'bg-red-50 text-red-900' : ''}`}>
      <label htmlFor={name} className={`${labelClassName} sm:w-1/4 py-2 px-3 text-sm shrink-0`}>
        {HTMLReactParser(capitalize(placeholder))}
        {required && <span className="p-1 text-red-600">*</span>}
      </label>

      <div className="sm:w-3/4 flex flex-col">
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue || ''}
          render={({ field: { onChange, value } }) => (
            <PhoneInputField
              name={name}
              id={name}
              required={required}
              value={value}
              onChange={onChange}
            />
          )}
        />
        {formState.errors[name] && (
          <div className="text-red-600 bg-red-50 rounded p-2 my-2 text-sm">
            Veuillez renseigner votre {capitalize(placeholder)}
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneFormRow;
